import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  TimelineChart,
  type TimelineData,
  type TimelineEntity,
} from '@revelio/replots';

import type { SubsidiaryLineage } from '../../DataProvider/subsidiaries/SubsidiaryLineageDataProvider';
import { SubsidiaryLineageDataProvider } from '../../DataProvider/subsidiaries/SubsidiaryLineageDataProvider';
import { useTrendDateRange } from '../../DataProvider/subsidiaries/use-subsidiary-dates';
import { useSharedSubsidiaryDrill } from '../../DataProvider/subsidiaries/use-subsidiary-drill';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { ChartFallback } from '../ChartFallback';
import { useDrilldownHeaderBreadcrumb } from '../ChartHeaderBreadcrumb';
import { ChartNotSupported } from '../ChartNotSupported';
import { formatIcicleHeadcount } from '../Icicle/icicle-format';
import { useLineageExport, useLineageYearHighlight } from './use-lineage-chart';

const DRILL_ROOT_ID = '__timeline_drill_root';
const OTHER_STEP_ID = '__timeline_other';

type DrillStep = {
  id: string;
  name: string;
  memberIds?: string[];
  color?: string;
};

type LineageIndex = {
  byId: Map<string, TimelineEntity>;
  childrenOf: Map<string, TimelineEntity[]>;
};

const indexLineage = (data: TimelineData): LineageIndex => {
  const byId = new Map(data.entities.map((entity) => [entity.id, entity]));
  const childrenOf = new Map<string, TimelineEntity[]>();
  for (const entity of data.entities) {
    if (entity.parentId === undefined || entity.parentId === entity.id) {
      continue;
    }
    const siblings = childrenOf.get(entity.parentId) ?? [];
    siblings.push(entity);
    childrenOf.set(entity.parentId, siblings);
  }
  return { byId, childrenOf };
};

const subtreesOf = (index: LineageIndex, ids: string[]): TimelineEntity[] => {
  const seen = new Set<string>();
  const walk = (entityId: string): TimelineEntity[] => {
    if (seen.has(entityId)) return [];
    seen.add(entityId);
    const entity = index.byId.get(entityId);
    return entity
      ? [
          entity,
          ...(index.childrenOf.get(entityId) ?? []).flatMap((child) =>
            walk(child.id)
          ),
        ]
      : [];
  };
  return ids.flatMap(walk);
};

const viewRootsOf = (step: DrillStep): string[] => step.memberIds ?? [step.id];

const drillView = (
  data: TimelineData,
  index: LineageIndex,
  rootIds: string[]
): TimelineData => {
  const roots = new Set(rootIds);
  const entities = subtreesOf(index, rootIds);
  if (entities.length === 0) return data;
  return {
    ...data,
    entities: [
      { id: DRILL_ROOT_ID },
      ...entities.map((entity) =>
        roots.has(entity.id) ? { ...entity, parentId: DRILL_ROOT_ID } : entity
      ),
    ],
  };
};

const ancestryOf = (index: LineageIndex, id: string): TimelineEntity[] => {
  const chain: TimelineEntity[] = [];
  const seen = new Set<string>();
  let cursor = index.byId.get(id);
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    const parent =
      cursor.parentId === undefined
        ? undefined
        : index.byId.get(cursor.parentId);
    if (parent === undefined) break;
    chain.unshift(cursor);
    cursor = parent;
  }
  return chain;
};

const trailWithin = (
  chain: TimelineEntity[],
  view: DrillStep | undefined
): DrillStep[] => {
  const roots = view === undefined ? [] : viewRootsOf(view);
  const at = chain.findIndex((entity) => roots.includes(entity.id));
  const start = at < 0 || view?.memberIds !== undefined ? at : at + 1;
  return chain
    .slice(Math.max(start, 0))
    .map((entity) => ({ id: entity.id, name: entity.label ?? entity.id }));
};

const otherStep = (
  index: LineageIndex,
  memberIds: string[],
  depth: number
): DrillStep => {
  const [lone] = memberIds;
  return {
    id: `${OTHER_STEP_ID}-${depth}`,
    name:
      memberIds.length === 1 && lone !== undefined
        ? (index.byId.get(lone)?.label ?? lone)
        : `${memberIds.length} smaller subsidiaries`,
    memberIds,
  };
};

const companyPathOf = (stack: DrillStep[]): number[] =>
  stack.flatMap((step) =>
    step.memberIds === undefined ? [Number(step.id)] : []
  );

/**
 * Applies the Over Time tab's Date Range selection: the axis bounds, plus the
 * membership — subsidiaries that joined after the window or left before it
 * (and everything under them) were not part of the group in that period.
 */
export const windowedTimelineData = (
  data: TimelineData,
  { start, end }: { start: string | null; end: string | null }
): TimelineData => {
  if (start === null && end === null) return data;

  const outside = new Set(
    data.entities
      .filter(
        (entity) =>
          (end !== null &&
            entity.joinedAt !== undefined &&
            entity.joinedAt > end) ||
          (start !== null &&
            entity.endedAt !== undefined &&
            entity.endedAt < start)
      )
      .map((entity) => entity.id)
  );
  const index = indexLineage(data);
  const dropped = new Set(
    subtreesOf(index, [...outside]).map((entity) => entity.id)
  );

  return {
    ...data,
    entities: data.entities.flatMap((entity) => {
      if (dropped.has(entity.id)) return [];
      if (
        end !== null &&
        entity.endedAt !== undefined &&
        entity.endedAt > end
      ) {
        const { endedAt: _endedAt, ...stillMember } = entity;
        return [stillMember];
      }
      return [entity];
    }),
    ...(start !== null && { startDate: start }),
    ...(end !== null && { endDate: end }),
  };
};

const withColor = (steps: DrillStep[], color: string | undefined) =>
  color === undefined ? steps : steps.map((step) => ({ ...step, color }));

const resolveDrill = (
  index: LineageIndex,
  path: number[],
  color: string | undefined
): DrillStep[] => {
  const target = [...path]
    .reverse()
    .find((rcid) => (index.childrenOf.get(String(rcid))?.length ?? 0) > 0);
  return target === undefined
    ? []
    : withColor(
        trailWithin(ancestryOf(index, String(target)), undefined),
        color
      );
};

const TimelineContent = ({
  data: rawData,
  rootName,
  rcid,
}: SubsidiaryLineage) => {
  const range = useTrendDateRange();
  const data = useMemo(
    () => windowedTimelineData(rawData, range),
    [rawData, range]
  );
  const highlight = useLineageYearHighlight(rcid);
  useLineageExport(data);

  const index = useMemo(() => indexLineage(data), [data]);
  const [stack, setStack] = useState<DrillStep[]>([]);
  const current = stack[stack.length - 1];
  const view = useMemo(
    () =>
      current === undefined
        ? data
        : drillView(data, index, viewRootsOf(current)),
    [data, index, current]
  );
  const viewTotal = useMemo(
    () =>
      current === undefined
        ? 0
        : subtreesOf(index, viewRootsOf(current))
            .filter((entity) => entity.endedAt === undefined)
            .reduce((sum, entity) => sum + (entity.value ?? 0), 0),
    [index, current]
  );

  const isDrillable = useCallback(
    (id: string) =>
      id !== current?.id && (index.childrenOf.get(id)?.length ?? 0) > 0,
    [index, current]
  );
  const {
    path: sharedPath,
    color: sharedColor,
    share,
  } = useSharedSubsidiaryDrill(rcid);
  useEffect(() => {
    if (sharedPath === undefined) return;
    setStack((previous) => {
      const own = companyPathOf(previous);
      if (isEqual(own, sharedPath)) return previous;
      const next = resolveDrill(index, sharedPath, sharedColor);
      return isEqual(own, companyPathOf(next)) ? previous : next;
    });
  }, [index, sharedPath, sharedColor]);

  const drillTo = useCallback(
    (next: DrillStep[]) => {
      setStack(next);
      share(companyPathOf(next), next.at(-1)?.color);
    },
    [share]
  );
  const handleDrill = useCallback(
    (id: string, memberIds: string[], laneColor: string | undefined) => {
      drillTo([
        ...stack,
        ...(memberIds.length > 0
          ? [otherStep(index, memberIds, stack.length)]
          : withColor(
              trailWithin(ancestryOf(index, id), stack.at(-1)),
              laneColor
            )),
      ]);
    },
    [drillTo, index, stack]
  );
  const handleNavigate = useCallback(
    (depth: number) => drillTo(stack.slice(0, depth)),
    [drillTo, stack]
  );

  useDrilldownHeaderBreadcrumb({
    stack,
    totalLabel: stack.length > 0 ? formatIcicleHeadcount(viewTotal) : null,
    onNavigate: handleNavigate,
  });

  if (data.entities.length <= 1) {
    return (
      <ChartNotSupported>
        {`${rootName} has no dated subsidiary history in our data.`}
      </ChartNotSupported>
    );
  }

  return (
    <TimelineChart
      data={view}
      color={current?.color}
      highlight={highlight}
      isDrillable={isDrillable}
      onDrill={handleDrill}
    />
  );
};

export const SubsidiariesTimeline = () => (
  <SubsidiaryLineageDataProvider>
    {({ data, status }) =>
      data === null ? (
        <ChartFallback status={DataProviderQueryStatus.Success}>
          <ChartNotSupported>
            Select a company to see when its subsidiaries joined.
          </ChartNotSupported>
        </ChartFallback>
      ) : (
        <ChartFallback status={status}>
          {data !== undefined && <TimelineContent key={data.rcid} {...data} />}
        </ChartFallback>
      )
    }
  </SubsidiaryLineageDataProvider>
);
